import { isLocalAnalyticsEnvironment } from "@/lib/analytics/environment";
import {
  isCanonicalAnalyticsAttributionValue,
  normalizeAnalyticsAttributionValue,
} from "@/lib/analytics/attributionValue";
import { logger } from "@/lib/logging/logger";
import { readDocumentCookies, writeDocumentCookie } from "@/lib/browserCookies";
import { tryDecodeURIComponent } from "@/lib/url";

// First-party cookies (not localStorage) so the same attribution reaches server-owned capture
// and OAuth chokepoints via the tRPC client's `credentials: "include"`. The backend validates
// these snapshots and binds them to the verified device + marketing session; business sign-in
// inputs never assemble attribution.
const COOKIE_NAMES = {
  first: "ee_attribution_first",
  last: "ee_attribution_last",
} as const;

// Matches the configured paid-click attribution lookback. First touch is write-once within this
// window; last touch is replaced atomically on a later attributed navigation.
const MAX_AGE_SECONDS = 90 * 24 * 60 * 60;

const isLocal = isLocalAnalyticsEnvironment();
const log = logger.child("AnalyticsAttribution");

// Wire shape throughout: `utm` keys are the raw `utm_*` query param names used by the capture API
// and persisted lead attribution, not the camelCase `UtmAttribution` used while parsing location
// state. `utm.ts` is the sole adapter between the two shapes.
type AttributionSnapshot = {
  captureKey: string;
  landingPath: string;
  referrerOrigin?: string;
  utm?: Record<string, string>;
  gclid?: string;
  msclkid?: string;
  gbraid?: string;
  wbraid?: string;
  /** First-party attribution only; never projected into provider-facing page URLs or events. */
  fbclid?: string;
  capturedAt: string; // ISO 8601
};

// Paid-click identifiers are far shorter in practice. Keeping every individual value bounded,
// then enforcing the percent-encoded aggregate below, prevents browsers silently dropping the
// whole ~4KB cookie when several decorated fields arrive together.
const ATTRIBUTION_COOKIE_MAX_ENCODED_LENGTH = 3800;
const ATTRIBUTION_OMITTED_LANDING_PATH = "/__attribution_path_omitted__";
/** Single source of the capture-key contract, mirrored by the API's zod/regex validation. */
export const CAPTURE_KEY_PATTERN = /^[A-Za-z0-9_-]{16,255}$/;
const UTM_WIRE_KEYS = new Set(["utm_source", "utm_medium", "utm_campaign", "utm_term", "utm_content"]);
const ATTRIBUTION_QUERY_KEYS = [...UTM_WIRE_KEYS, "gclid", "msclkid", "gbraid", "wbraid", "fbclid"] as const;
export const ATTRIBUTION_HISTORY_STATE_KEY = "__eeAnalyticsAttribution";

const readCookieValue = (name: string): string | undefined => {
  const cookie = readDocumentCookies();
  for (const part of cookie ? cookie.split(/;\s*/) : []) {
    const separatorIndex = part.indexOf("=");
    const key = tryDecodeURIComponent(separatorIndex > -1 ? part.slice(0, separatorIndex) : part);
    if (key === name) {
      return tryDecodeURIComponent(separatorIndex > -1 ? part.slice(separatorIndex + 1) : "");
    }
  }
  return undefined;
};

const writeCookieValue = (name: string, value: string) => {
  const isHttps = typeof window !== "undefined" && window.location.protocol === "https:";
  // Secure is dropped outside https: contexts — a browser refuses to set the cookie at all
  // (not just the flag) when Secure is present on an insecure origin, which would silently
  // break local dev over http://localhost.
  writeDocumentCookie(
    `${name}=${encodeURIComponent(value)}; Path=/; Max-Age=${MAX_AGE_SECONDS}; SameSite=Lax${isHttps ? "; Secure" : ""}`,
  );
};

const deleteCookie = (name: string): void => {
  const isHttps = typeof window !== "undefined" && window.location.protocol === "https:";
  writeDocumentCookie(`${name}=; Path=/; Max-Age=0; SameSite=Lax${isHttps ? "; Secure" : ""}`);
};

const safeParseSnapshot = (value: string | undefined): AttributionSnapshot | undefined => {
  if (!value) return undefined;
  try {
    const parsed: unknown = JSON.parse(value);
    return isAttributionSnapshotShape(parsed) ? parsed : undefined;
  } catch (error) {
    if (isLocal) {
      log.warn("Failed to parse persisted attribution cookie", { error, suppressTelemetry: true });
    }
    return undefined;
  }
};

function isAttributionSnapshotShape(value: unknown): value is AttributionSnapshot {
  if (!isPlainObject(value)) return false;
  if (
    Object.keys(value).some(
      (key) =>
        ![
          "captureKey",
          "landingPath",
          "referrerOrigin",
          "utm",
          "gclid",
          "msclkid",
          "gbraid",
          "wbraid",
          "fbclid",
          "capturedAt",
        ].includes(key),
    )
  ) {
    return false;
  }

  if (typeof value.captureKey !== "string" || !CAPTURE_KEY_PATTERN.test(value.captureKey)) return false;
  if (typeof value.landingPath !== "string" || !value.landingPath.startsWith("/") || value.landingPath.length > 2000) {
    return false;
  }
  if (value.referrerOrigin !== undefined && !isHttpOrigin(value.referrerOrigin)) return false;
  if (
    typeof value.capturedAt !== "string" ||
    value.capturedAt.length > 64 ||
    !Number.isFinite(Date.parse(value.capturedAt))
  ) {
    return false;
  }

  for (const key of ["gclid", "msclkid", "gbraid", "wbraid", "fbclid"] as const) {
    const field = value[key];
    if (field !== undefined && !isBoundedValue(field)) return false;
  }

  if (value.utm !== undefined) {
    if (!isPlainObject(value.utm)) return false;
    for (const [key, field] of Object.entries(value.utm)) {
      if (!UTM_WIRE_KEYS.has(key) || !isBoundedValue(field)) return false;
    }
  }

  return true;
}

const isPlainObject = (value: unknown): value is Record<string, unknown> =>
  typeof value === "object" && value !== null && !Array.isArray(value);

const isBoundedValue = (value: unknown): value is string => isCanonicalAnalyticsAttributionValue(value);

const isHttpOrigin = (value: unknown): value is string => {
  if (typeof value !== "string" || value.length > 255) return false;
  try {
    const url = new URL(value);
    return (url.protocol === "http:" || url.protocol === "https:") && url.origin === value;
  } catch {
    return false;
  }
};

const writeSnapshot = (cookieName: string, snapshot: AttributionSnapshot) => {
  try {
    const cookieSafeSnapshot = toCookieSafeSnapshot(snapshot);
    if (!cookieSafeSnapshot) return;
    writeCookieValue(cookieName, JSON.stringify(cookieSafeSnapshot));
  } catch (error) {
    if (isLocal) {
      log.warn("Failed to persist attribution cookie", { error, suppressTelemetry: true });
    }
  }
};

/**
 * Fits one whole immutable snapshot under the practical cookie limit. Exact click ids are kept
 * first (never truncated), followed by source/medium/campaign and then optional UTM/referrer
 * dimensions. A field that does not fit is omitted atomically.
 */
function toCookieSafeSnapshot(snapshot: AttributionSnapshot): AttributionSnapshot | undefined {
  let result: AttributionSnapshot = {
    captureKey: snapshot.captureKey,
    capturedAt: snapshot.capturedAt,
    landingPath: snapshot.landingPath,
  };
  const fits = (candidate: AttributionSnapshot) =>
    encodeURIComponent(JSON.stringify(candidate)).length <= ATTRIBUTION_COOKIE_MAX_ENCODED_LENGTH;
  if (!fits(result)) {
    // A path can be within the server's character limit yet explode when percent-encoded
    // (e.g. Unicode-heavy slugs). Preserve the paid-click observation under the cookie limit;
    // the explicit sentinel is safer for analysts than pretending the landing was `/`.
    result = { ...result, landingPath: ATTRIBUTION_OMITTED_LANDING_PATH };
  }
  if (!fits(result)) return undefined;

  const addTopLevel = (key: "gclid" | "msclkid" | "gbraid" | "wbraid" | "fbclid" | "referrerOrigin") => {
    const value = snapshot[key];
    if (!value || !isBoundedValue(value)) return;
    const candidate = { ...result, [key]: value };
    if (fits(candidate)) result = candidate;
  };
  for (const key of ["gclid", "msclkid", "gbraid", "wbraid", "fbclid"] as const) addTopLevel(key);

  for (const key of ["utm_source", "utm_medium", "utm_campaign", "utm_term", "utm_content"] as const) {
    const value = snapshot.utm?.[key];
    if (!value || !isBoundedValue(value)) continue;
    const candidate = { ...result, utm: { ...(result.utm ?? {}), [key]: value } };
    if (fits(candidate)) result = candidate;
  }
  addTopLevel("referrerOrigin");

  return hasAttributionValue(result) ? result : undefined;
}

export const getPersistedFirstTouchAttribution = (): AttributionSnapshot | undefined =>
  safeParseSnapshot(readCookieValue(COOKIE_NAMES.first));

export const getPersistedLastTouchAttribution = (): AttributionSnapshot | undefined =>
  safeParseSnapshot(readCookieValue(COOKIE_NAMES.last));

/**
 * The last touch when this page load captured it, rather than an earlier page load. `capturedAt`
 * is truncated to the millisecond and `timeOrigin` is not, hence the floor.
 */
export const getLastTouchCapturedByThisPageLoad = (): AttributionSnapshot | undefined => {
  const last = getPersistedLastTouchAttribution();
  return last && Date.parse(last.capturedAt) >= Math.floor(performance.timeOrigin) ? last : undefined;
};

type AttributionFields = Partial<Omit<AttributionSnapshot, "capturedAt">>;

const hasAttributionValue = (fields: AttributionFields): boolean =>
  Boolean(
    fields.gclid ||
    fields.msclkid ||
    fields.gbraid ||
    fields.wbraid ||
    fields.fbclid ||
    fields.referrerOrigin ||
    (fields.utm && Object.values(fields.utm).some(Boolean)),
  );

/** One browser-generated idempotency key per exact landing snapshot; shared by all writers. */
export const generateCaptureKey = (): string => {
  if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
    return `cap_${crypto.randomUUID()}`;
  }
  return `cap_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 18)}`;
};

const completeSnapshot = (fields: AttributionFields, capturedAt = new Date().toISOString()): AttributionSnapshot => ({
  ...fields,
  captureKey:
    fields.captureKey && CAPTURE_KEY_PATTERN.test(fields.captureKey) ? fields.captureKey : generateCaptureKey(),
  landingPath: fields.landingPath?.startsWith("/") ? fields.landingPath.slice(0, 2000) : "/",
  capturedAt,
});

type AttributionCaptureIdentity = Pick<AttributionSnapshot, "captureKey" | "capturedAt">;

/**
 * Persists one navigation as one immutable observation. Both cookies receive the exact same
 * capture key and timestamp; a second analytics effect observing that navigation reuses the
 * same identity instead of creating a conflicting payload for the server idempotency key.
 *
 * The first-touch cookie is write-once (never overwritten once set); the last-touch cookie is
 * replaced in full every call. Attribution is one atomic observation: carrying UTM values from
 * one navigation into a later click-id-only navigation (or vice versa) creates a synthetic touch
 * that never happened and can mis-credit a conversion.
 */
export const persistAttributionTouch = (
  fields: Omit<AttributionFields, "captureKey">,
  identity: AttributionCaptureIdentity,
): void => {
  if (!hasAttributionValue(fields)) return;
  const snapshot = completeSnapshot({ ...fields, captureKey: identity.captureKey }, identity.capturedAt);
  if (!getPersistedFirstTouchAttribution()) writeSnapshot(COOKIE_NAMES.first, snapshot);
  writeSnapshot(COOKIE_NAMES.last, snapshot);
};

/**
 * Clears attribution at an authenticated-owner boundary: logout, an account switch, or an expired
 * session noticed on a later page load. Ordinary browser/session-cookie expiry leaves these
 * browser-owned cookies intact for their requested 90-day lifetime, though privacy features may
 * shorten it. The API deliberately does not refresh them because a delayed response could cross a
 * later account boundary.
 */
export const clearPersistedAttributionAtAuthBoundary = (): void => {
  deleteCookie(COOKIE_NAMES.first);
  deleteCookie(COOKIE_NAMES.last);

  stripAttributionFromCurrentHistoryEntry();
};

/**
 * Removes attribution from the active history entry without disturbing application-owned state or
 * unrelated query parameters. The navigation marker is removed as well because it contains the
 * prior journey's attribution fingerprint and capture identity.
 */
export const stripAttributionFromCurrentHistoryEntry = (): void => {
  if (typeof window === "undefined") return;
  try {
    const url = new URL(window.location.href);
    let changed = false;
    for (const key of ATTRIBUTION_QUERY_KEYS) {
      if (!url.searchParams.has(key)) continue;
      url.searchParams.delete(key);
      changed = true;
    }

    let historyState = window.history.state as unknown;
    if (isPlainObject(historyState) && Object.hasOwn(historyState, ATTRIBUTION_HISTORY_STATE_KEY)) {
      const nextHistoryState = { ...historyState };
      delete nextHistoryState[ATTRIBUTION_HISTORY_STATE_KEY];
      historyState = nextHistoryState;
      changed = true;
    }
    if (changed) {
      window.history.replaceState(historyState, "", `${url.pathname}${url.search}${url.hash}`);
    }
  } catch {
    // Callers remain fail-closed at the cookie/session boundary when History or URL APIs fail.
  }
};

/** Extracts paid-click identifiers from a location.search string. UTM extraction stays in utm.ts. */
export const extractClickIdsFromLocation = (
  locationSearch: string,
): Partial<Pick<AttributionSnapshot, "gclid" | "msclkid" | "gbraid" | "wbraid" | "fbclid">> | undefined => {
  const params = new URLSearchParams(locationSearch);
  const gclid = normalizeAttributionValue(params.get("gclid"));
  const msclkid = normalizeAttributionValue(params.get("msclkid"));
  const gbraid = normalizeAttributionValue(params.get("gbraid"));
  const wbraid = normalizeAttributionValue(params.get("wbraid"));
  const fbclid = normalizeAttributionValue(params.get("fbclid"));
  if (!gclid && !msclkid && !gbraid && !wbraid && !fbclid) return undefined;
  return {
    ...(gclid ? { gclid } : {}),
    ...(msclkid ? { msclkid } : {}),
    ...(gbraid ? { gbraid } : {}),
    ...(wbraid ? { wbraid } : {}),
    ...(fbclid ? { fbclid } : {}),
  };
};

const normalizeAttributionValue = (value: string | null): string | undefined => {
  return normalizeAnalyticsAttributionValue(value);
};
